/**
 * AgentTaskSubmissionsView — DEPRECATED old lifecycle (agent_task_submissions).
 *
 * As of iter-172 (Feb 2026), AI-generated agent tasks are UNIFIED with the
 * manual assigned-tasks lifecycle.  This view now renders the same list as
 * /dashboard/cloudgm/assigned-tasks/list?source=ai_generated but embedded in
 * the manage-platform tab so the existing navigation ("متابعة مهام الوكلاء")
 * still works.
 *
 * Row click → the same detail page used by manual tasks
 *             (/dashboard/cloudgm/assigned-tasks/:id).
 * The detail page adds a "سجل المحادثة" (Chat history) section at the top
 * of the Request tab when task.source === 'ai_generated'.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Bot, User, Store, RefreshCw, Loader2, Search,
  Clock, CheckCircle2, AlertCircle, XCircle, Lock,
  ChevronLeft, ChevronRight, Paperclip, Briefcase,
} from 'lucide-react';
import { getAuthHeaders } from '../../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  pending:     { ar: 'قيد الانتظار', en: 'Pending',     color: '#f59e0b', Icon: Clock },
  in_progress: { ar: 'قيد التنفيذ',  en: 'In progress', color: '#3b82f6', Icon: AlertCircle },
  completed:   { ar: 'مكتملة',       en: 'Completed',   color: '#10b981', Icon: CheckCircle2 },
  cancelled:   { ar: 'ملغاة',        en: 'Cancelled',   color: '#ef4444', Icon: XCircle },
};

// iter-217c-v13 — tiny debounce hook (no external dep). Keeps the cache key
// stable while the user is typing so we don't spam the network on every keystroke.
function useDebounced(value, delay = 300) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

export default function AgentTaskSubmissionsView({ language = 'ar' }) {
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  // iter-208 — buckets mirror the 3 in-task stages (Active / In-progress /
  // Closed) that the admin sees inside the request. "closed" here means the
  // task is COMPLETED and CLOSED (finalised — stage 3 done).
  const [view, setView] = useState('active');   // active | in_progress | closed | all
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);   // iter-217 — surface CF/network errors instead of an empty screen
  const [q, setQ] = useState('');

  // iter-217c-v13 — stale-while-revalidate cache. Switching tabs was slow
  // on production because every click did a full round-trip while blanking
  // the UI. Now: (a) if this (view, q) key was fetched before, we render
  // the cached rows INSTANTLY, then silently refresh in the background —
  // no spinner flash, no blank state. (b) The search box is debounced so
  // fast typing doesn't spam the network. (c) On CF error we KEEP the
  // previous rows visible so the admin can still work.
  const cacheRef = useRef(new Map()); // key → { data, ts }
  const debouncedQ = useDebounced(q, 350);
  const cacheKey = `agent|${view}|${debouncedQ.trim()}`;

  const load = useCallback(async () => {
    const cached = cacheRef.current.get(cacheKey);
    if (cached) {
      setRows(cached.data);
      setLoadError(null);
      setLoading(false);
      if (Date.now() - cached.ts < 30_000) return; // fresh enough, skip refetch
    } else {
      setLoading(true);
    }
    setLoadError(null);
    try {
      const params = {
        // Feb 2026: unified view — no `source` filter so BOTH manually-added
        // agent tasks (via the "إدراج مهمة لوكيل (يدوي)" card) AND AI-generated
        // ones (from the sub-platform assistant) show up together.
        target_type: 'agent',
        view,
        limit: 100,             // iter-217 — explicit cap (matches server default)
      };
      if (debouncedQ.trim()) params.q = debouncedQ.trim();
      const r = await axios.get(`${API_URL}/api/admin/assigned-tasks`, {
        headers: getAuthHeaders(), params,
        // 30s timeout — a healthy list request takes < 500ms; anything above
        // this is a symptom of the server struggling (was CF 520 in prod).
        timeout: 30_000,
      });
      const list = r.data.data || [];
      cacheRef.current.set(cacheKey, { data: list, ts: Date.now() });
      setRows(list);
    } catch (e) {
      // iter-217 — Cloudflare 520 returns HTML (not JSON), so `e.response.data.detail`
      // is undefined. Fall back to a friendly message and keep the previous rows
      // visible so the admin can still see what was there before the retry.
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail
        || (status ? `${t('فشل التحميل', 'Load failed')} (HTTP ${status})` : t('تعذّر الاتصال بالخادم', 'Could not reach the server'));
      setLoadError(msg);
      // iter-217c-v13 — no more toast spam on filter switching; the banner
      // already tells the user something is wrong.
      if (!cached) toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [view, debouncedQ, cacheKey, isAr]);

  useEffect(() => { load(); }, [load]);

  // iter-217c-v13 — Prefetch the OTHER tabs 800 ms after the current tab
  // finishes loading. On production the response is now < 150 KB per iter-
  // 217c-v11, so a warm cache for all 4 tabs costs <1 MB and makes tab
  // switching feel INSTANT (rendered from cache immediately, background
  // refresh keeps it fresh). Skipped if already cached.
  useEffect(() => {
    if (loading || loadError) return;
    const others = ['active', 'in_progress', 'closed', 'all'].filter((v) => v !== view);
    const t = setTimeout(() => {
      others.forEach(async (otherView) => {
        const otherKey = `agent|${otherView}|${debouncedQ.trim()}`;
        if (cacheRef.current.has(otherKey)) return;
        try {
          const params = { target_type: 'agent', view: otherView, limit: 100 };
          if (debouncedQ.trim()) params.q = debouncedQ.trim();
          const r = await axios.get(`${API_URL}/api/admin/assigned-tasks`, {
            headers: getAuthHeaders(), params, timeout: 30_000,
          });
          cacheRef.current.set(otherKey, { data: r.data.data || [], ts: Date.now() });
        } catch { /* silent prefetch */ }
      });
    }, 800);
    return () => clearTimeout(t);
  }, [view, debouncedQ, loading, loadError]);

  return (
    <div dir={isAr ? 'rtl' : 'ltr'} data-testid="agent-task-submissions">
      {/* Header */}
      <div className="mb-5 flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-purple-500/15 border border-purple-500/40 flex items-center justify-center">
          <Bot className="w-6 h-6 text-purple-300" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-xl font-bold text-white">
            {t('متابعة مهام الوكلاء', 'Track Agent Tasks')}
          </h2>
          <p className="text-xs text-white/50">
            {t('كل مهام الوكلاء في مكان واحد — اليدوية والقادمة من الموقع ومن الذكاء الاصطناعي. المهام من الذكاء الاصطناعي تعرض سجل المحادثة داخل الطلب.',
               'All agent tasks in one place — manual, from the site, and AI-generated. AI-generated tasks show the chat transcript inside the Request tab.')}
          </p>
        </div>
        <button onClick={load} disabled={loading}
                className="px-3 h-9 rounded-lg bg-white/5 border border-white/10 text-white text-sm hover:bg-white/10 flex items-center gap-2">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('تحديث', 'Refresh')}
        </button>
      </div>

      {/* Filter buttons — iter-208 — mirror the 3 in-request stages */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
        <FilterButton active={view === 'active'}      onClick={() => setView('active')}      label={t('فعّال', 'Active')}                       colour="#f59e0b" />
        <FilterButton active={view === 'in_progress'} onClick={() => setView('in_progress')} label={t('قيد التنفيذ', 'In progress')}            colour="#3b82f6" />
        <FilterButton active={view === 'closed'}      onClick={() => setView('closed')}      label={t('تم الإنتهاء وإغلاق الطلب', 'Completed & closed')} colour="#10b981" />
        <FilterButton active={view === 'all'}         onClick={() => setView('all')}         label={t('الكل', 'All')}                          colour="#71717a" />
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute inset-y-0 start-3 my-auto w-4 h-4 text-white/40" />
        <input value={q} onChange={(e) => setQ(e.target.value)}
               placeholder={t('ابحث بالوكيل / الخدمة / رقم الطلب…', 'Search agent / service / request no…')}
               className="w-full bg-[#050505] border border-white/10 text-white rounded-md h-10 ps-9 pe-3 text-sm" />
      </div>

      {/* iter-217 — visible error banner so admins don't confuse a Cloudflare
          520 / network drop with an actually-empty bucket. */}
      {loadError && (
        <div className="mb-4 flex items-start gap-3 p-3 rounded-xl border border-rose-500/40 bg-rose-500/10"
             data-testid="agent-tasks-load-error">
          <AlertCircle className="w-5 h-5 text-rose-300 flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm text-rose-100 font-semibold">
              {t('تعذّر تحميل المهام', 'Could not load tasks')}
            </p>
            <p className="text-xs text-rose-200/80 mt-0.5 break-words">{loadError}</p>
          </div>
          <button onClick={load}
                  className="px-3 h-8 rounded-lg text-xs font-semibold border border-rose-400/40 text-rose-100 hover:bg-rose-500/20 flex-shrink-0"
                  data-testid="agent-tasks-retry-btn">
            {t('إعادة المحاولة', 'Retry')}
          </button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="p-16 flex justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-white/40" />
        </div>
      ) : rows.length === 0 ? (
        <div className="p-16 text-center bg-white/[0.02] border border-dashed border-white/10 rounded-2xl">
          <Bot className="w-12 h-12 text-white/20 mx-auto mb-2" />
          <p className="text-white/50 text-sm">
            {loadError
              ? t('لم يتم عرض المهام بسبب خطأ في التحميل.', 'Tasks are hidden because of a load error.')
              : t('لا توجد مهام للوكلاء في هذا التبويب.', 'No agent tasks in this bucket.')}
          </p>
          <p className="text-white/30 text-[11px] mt-1">
            {t('عند إدراج مهمة يدوياً أو وصول طلب جديد من الموقع/المساعد الذكي ستظهر هنا فوراً.',
               'When a task is added manually or arrives from the site/AI assistant it will appear here immediately.')}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-white/5 bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {rows.map(row => (
            <SubmissionRow key={row.id}
                           row={row}
                           onOpen={() => navigate(`/dashboard/cloudgm/assigned-tasks/${row.id}`)}
                           t={t} isAr={isAr} Arrow={Arrow} />
          ))}
        </div>
      )}
    </div>
  );
}


function FilterButton({ active, onClick, label, colour }) {
  return (
    <button onClick={onClick}
            className="h-10 rounded-lg text-xs font-semibold border transition"
            style={active
              ? { backgroundColor: `${colour}22`, borderColor: `${colour}88`, color: colour }
              : { backgroundColor: 'rgba(255,255,255,0.02)', borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)' }}>
      {label}
    </button>
  );
}


function SubmissionRow({ row, onOpen, t, isAr, Arrow }) {
  const status = STATUS_META[row.status] || STATUS_META.pending;
  const StatusIcon = status.Icon;
  const isFinalised = row.review_status === 'closed';
  // Same purple accent everywhere so the layout stays uniform; the source
  // badge below the title makes it obvious whether the task came from a
  // human admin, the sub-platform site, or the AI assistant.
  const accent = '#8b5cf6';
  const isAI = row.source === 'ai_generated';
  const LeadIcon = isAI ? Bot : User;

  return (
    <div onClick={onOpen}
         role="button" tabIndex={0}
         onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}
         className="p-4 hover:bg-white/[0.03] transition-colors cursor-pointer"
         data-testid={`agent-submission-row-${row.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isAI ? 'bg-purple-500/20 border border-purple-500/40' : 'bg-blue-500/20 border border-blue-500/40'}`}>
          <LeadIcon className={`w-5 h-5 ${isAI ? 'text-purple-300' : 'text-blue-300'}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            {row.request_no_display && (
              <span className="text-[11px] font-mono px-2 py-0.5 rounded-md border"
                    style={{ backgroundColor: `${accent}20`, borderColor: `${accent}40`, color: accent }}>
                {row.request_no_display}
              </span>
            )}
            {isAI ? (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-purple-500/20 text-purple-300 border border-purple-500/40 inline-flex items-center gap-1"
                    data-testid="badge-source-ai">
                <Bot className="w-3 h-3" />
                {t('من الذكاء الاصطناعي', 'AI-generated')}
              </span>
            ) : (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-blue-500/20 text-blue-300 border border-blue-500/40 inline-flex items-center gap-1"
                    data-testid="badge-source-manual">
                <User className="w-3 h-3" />
                {t('يدوي', 'Manual')}
              </span>
            )}
            <h3 className="text-white font-semibold text-sm">
              {isAr ? row.service_name_ar : (row.service_name_en || row.service_name_ar)}
            </h3>
            {/* iter-218 (Feb 2026) — Removed the "للتدقيق" (ready_for_review)
                chip from list rows. Admins found it confusing because the
                Status pill already shows "مكتملة" once the closed stage is
                ticked, and the intermediate "waiting-for-audit" state is
                only meaningful inside the detail page. Keep only the final
                "مغلقة" chip (review_status='closed'). */}
            {isFinalised && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-medium bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 inline-flex items-center gap-1">
                <Lock className="w-3 h-3" />
                {t('مغلقة', 'Locked')}
              </span>
            )}
          </div>
          {/* iter-227 (Feb 2026) — Sub-service on its own line so it can
              never be missed. Cyan tint to visually separate from the main
              service title. Same pattern as AssignedTasksListPage. */}
          {(isAr ? row.sub_service_name_ar : (row.sub_service_name_en || row.sub_service_name_ar)) && (
            <div className="text-[11px] text-cyan-300/80 mb-1 inline-flex items-center gap-1"
                 data-testid={`row-sub-service-${row.id}`}>
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400/60" />
              {t('الخدمة الفرعية', 'Sub-service')}:
              <span className="text-white/90">
                {isAr
                  ? (row.sub_service_name_ar || row.sub_service_name_en)
                  : (row.sub_service_name_en || row.sub_service_name_ar)}
              </span>
            </div>
          )}
          {/* iter-217c-v2 — TWO rows: (1) Beneficiary (`requestor_name`,
              the person the request is FOR — dash if empty) and
              (2) Agent (`target_name` + `target_email`, the one handling it).
              Both are shown BEFORE opening the task so the admin can
              triage from the list without clicking in. */}
          <div className="flex items-center gap-1.5 mb-0.5" data-testid={`row-beneficiary-${row.id}`}>
            <User className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
            <span className="text-[11px] text-amber-300/80 font-medium">
              {t('المقدم لأجله', 'For')}:
            </span>
            <span className={`text-sm truncate ${row.requestor_name ? 'text-white font-semibold' : 'text-white/40 italic'}`}>
              {row.requestor_name || '—'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 mb-1 flex-wrap" data-testid={`row-agent-${row.id}`}>
            <Briefcase className="w-3.5 h-3.5 text-blue-400 flex-shrink-0" />
            <span className="text-[11px] text-blue-300/80 font-medium">
              {t('الوكيل', 'Agent')}:
            </span>
            <span className="text-sm text-white font-semibold truncate">
              {row.target_name || '—'}
            </span>
            {row.target_email && (
              <span className="text-[11px] text-white/40 font-mono truncate" dir="ltr">
                · {row.target_email}
              </span>
            )}
          </div>
          {row.target_phone && (
            <div className="text-white/40 text-[11px] font-mono" dir="ltr">
              {row.target_phone}
            </div>
          )}
          {row.request_note && (
            <div className="text-white/50 text-xs mt-1 line-clamp-1 italic">
              &ldquo;{row.request_note}&rdquo;
            </div>
          )}
          {(row.attachments_count || (row.attachments || []).length) > 0 && (
            <div className="text-[11px] text-amber-400 mt-1 inline-flex items-center gap-1">
              <Paperclip className="w-3 h-3" />
              {row.attachments_count || row.attachments.length} {t('مرفق', 'attachments')}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-xs px-2.5 py-1 rounded-md border flex items-center gap-1"
                style={{ color: status.color, borderColor: `${status.color}55`, backgroundColor: `${status.color}15` }}>
            <StatusIcon className="w-3 h-3" />
            {isAr ? status.ar : status.en}
          </span>
          <Arrow className="w-4 h-4 text-white/30" />
        </div>
      </div>
    </div>
  );
}
